/**
 * The authoring canvas.
 *
 * Three things it does that the first version did not:
 *
 * 1. **Arrows show their direction.** The old line ran centre to centre, so the
 *    arrowhead landed underneath the target box and nothing on the diagram said
 *    which way the work flowed. Lines are clipped to both borders now (see
 *    `edgeGeometry`) and every arrow carries a head plus a mid-line chevron.
 * 2. **Connecting is announced.** The affordance was an unlabelled blue dot,
 *    and it was missed. There are two ways now and both say what they are: a
 *    handle labelled "drag to connect" on the selected box, and a Connect mode
 *    in the toolbar where you click one box then another.
 * 3. **Shape carries meaning.** A step, an entity, a data store, another
 *    process and an outside party are five different outlines, so the picture
 *    is readable before anything is clicked.
 *
 * It pans and zooms because a real process is wider than a screen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { FlowDoc, FlowEdge, FlowNode, NodeKind } from '@/api/processflow'
import {
  EDGE_STYLE, EdgeArrowDefs, GridBackdrop, KIND_STYLE, NODE_H, NODE_W,
  NodeOutline, clip, edgeGeometry, snap,
} from './shapes'

interface Props {
  flow: FlowDoc
  derived: { nodes: FlowNode[]; edges: FlowEdge[] }
  showDerived: boolean
  selection: { kind: 'node' | 'edge'; id: string } | null
  /** Node / edge ids carrying a finding, so the canvas can mark the thing the
   *  Issues list is talking about instead of leaving it to be hunted for. */
  problems: Record<string, 'error' | 'warning' | 'info'>
  titles: Record<string, string>
  connecting: boolean
  onSelect: (sel: { kind: 'node' | 'edge'; id: string } | null) => void
  onMoveNode: (id: string, x: number, y: number) => void
  onConnect: (from: string, to: string) => void
  onConnectingChange: (on: boolean) => void
  onDeleteNode: (id: string) => void
}

const MIN_ZOOM = 0.35
const MAX_ZOOM = 2.2

export default function FlowCanvas({
  flow, derived, showDerived, selection, problems, titles, connecting,
  onSelect, onMoveNode, onConnect, onConnectingChange, onDeleteNode,
}: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [view, setView] = useState({ x: 0, y: 0, z: 1 })
  const [drag, setDrag] = useState<{ id: string; dx: number; dy: number } | null>(null)
  const [pan, setPan] = useState<{ x: number; y: number; vx: number; vy: number } | null>(null)
  const [wire, setWire] = useState<{ from: string; x: number; y: number } | null>(null)
  const moved = useRef(false)

  const nodes = flow.nodes || []
  const edges = flow.edges || []
  const byId = useMemo(() => {
    const m = new Map<string, FlowNode>()
    for (const n of nodes) m.set(n.id, n)
    return m
  }, [nodes])

  const derivedShown = useMemo(
    () => (showDerived ? derived.edges.filter((e) => byId.has(e.from) && byId.has(e.to)) : []),
    [derived.edges, byId, showDerived])

  const extent = useMemo(() => {
    let w = 900
    let h = 520
    for (const n of nodes) {
      w = Math.max(w, n.x + NODE_W + 120)
      h = Math.max(h, n.y + NODE_H + 120)
    }
    return { w, h }
  }, [nodes])

  /** Client point → diagram point, through the current pan and zoom. */
  const toDiagram = useCallback((e: { clientX: number; clientY: number }) => {
    const r = svgRef.current?.getBoundingClientRect()
    return {
      x: ((e.clientX - (r?.left || 0)) - view.x) / view.z,
      y: ((e.clientY - (r?.top || 0)) - view.y) / view.z,
    }
  }, [view])

  const onMove = useCallback((e: React.MouseEvent) => {
    if (drag) {
      const p = toDiagram(e)
      moved.current = true
      onMoveNode(drag.id, snap(p.x - drag.dx), snap(p.y - drag.dy))
    } else if (pan) {
      setView((v) => ({ ...v, x: pan.vx + (e.clientX - pan.x), y: pan.vy + (e.clientY - pan.y) }))
    } else if (wire) {
      const p = toDiagram(e)
      setWire({ ...wire, x: p.x, y: p.y })
    }
  }, [drag, pan, wire, toDiagram, onMoveNode])

  const hitTest = useCallback((p: { x: number; y: number }) => nodes.find(
    (n) => p.x >= n.x && p.x <= n.x + NODE_W && p.y >= n.y && p.y <= n.y + NODE_H), [nodes])

  const onUp = useCallback((e: React.MouseEvent) => {
    if (wire) {
      const hit = hitTest(toDiagram(e))
      if (hit && hit.id !== wire.from) onConnect(wire.from, hit.id)
      setWire(null)
    }
    setDrag(null)
    setPan(null)
  }, [wire, hitTest, toDiagram, onConnect])

  // Zoom about the pointer, so the thing under the cursor stays under it.
  const onWheel = useCallback((e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey && Math.abs(e.deltaY) < 2) return
    const r = svgRef.current?.getBoundingClientRect()
    const cx = e.clientX - (r?.left || 0)
    const cy = e.clientY - (r?.top || 0)
    setView((v) => {
      const z = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.z * (e.deltaY > 0 ? 0.92 : 1.08)))
      const k = z / v.z
      return { z, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k }
    })
  }, [])

  const fit = useCallback(() => {
    const r = svgRef.current?.getBoundingClientRect()
    if (!r || !nodes.length) { setView({ x: 0, y: 0, z: 1 }); return }
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity
    for (const n of nodes) {
      minX = Math.min(minX, n.x); minY = Math.min(minY, n.y)
      maxX = Math.max(maxX, n.x + NODE_W); maxY = Math.max(maxY, n.y + NODE_H)
    }
    const z = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM,
      Math.min((r.width - 60) / (maxX - minX), (r.height - 60) / (maxY - minY), 1.2)))
    setView({ z, x: 30 - minX * z, y: 30 - minY * z })
  }, [nodes])

  // Escape leaves connect mode wherever the focus is — a mode you cannot get
  // out of is worse than no mode.
  useEffect(() => {
    if (!connecting && !wire) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setWire(null)
      onConnectingChange(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [connecting, wire, onConnectingChange])

  const problemOf = (id: string) => problems[id]

  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 0, minWidth: 0 }}>
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onWheel={onWheel}
        onMouseDown={(e) => {
          if (e.target !== e.currentTarget && !(e.target as Element).classList?.contains('pf-bg')) return
          onSelect(null)
          setPan({ x: e.clientX, y: e.clientY, vx: view.x, vy: view.y })
        }}
        style={{
          display: 'block', background: 'var(--color-bg, #f4f6f9)',
          cursor: connecting ? 'crosshair' : pan ? 'grabbing' : 'grab',
        }}
      >
        <defs>
          <GridBackdrop id="pf-grid" />
          <EdgeArrowDefs prefix="pf-head" />
        </defs>

        <rect className="pf-bg" x={0} y={0} width="100%" height="100%" fill="url(#pf-grid)" />

        <g transform={`translate(${view.x},${view.y}) scale(${view.z})`}>
          {/* Derived first, so authored arrows sit on top of them. */}
          {derivedShown.map((e) => {
            const a = byId.get(e.from); const b = byId.get(e.to)
            if (!a || !b) return null
            const st = EDGE_STYLE[e.kind] || EDGE_STYLE.reference
            const g = edgeGeometry(a, b)
            return (
              <path key={`d-${e.id}`} d={g.d} fill="none" stroke={st.stroke}
                strokeWidth={st.width} strokeDasharray={st.dash}
                markerEnd={`url(#pf-head-${EDGE_STYLE[e.kind] ? e.kind : 'reference'})`} />
            )
          })}

          {edges.map((e) => {
            const a = byId.get(e.from); const b = byId.get(e.to)
            if (!a || !b) return null
            const st = EDGE_STYLE[e.kind] || EDGE_STYLE.work
            const g = edgeGeometry(a, b)
            const active = selection?.kind === 'edge' && selection.id === e.id
            const bad = problemOf(e.id)
            const text = e.kind === 'work'
              ? (e.label || e.rule?.target?.verb || e.rule?.name || 'next step')
              : (e.label || st.label.toLowerCase())
            const quiet = e.kind === 'work' && e.raises_task === false
            return (
              <g key={e.id} style={{ cursor: 'pointer' }}
                onMouseDown={(ev) => { ev.stopPropagation(); onSelect({ kind: 'edge', id: e.id }) }}>
                {/* A fat invisible line, so an arrow can be clicked without
                    asking anybody to hit a 2px target. */}
                <path d={g.d} fill="none" stroke="transparent" strokeWidth={14} />
                <path
                  d={g.d} fill="none" stroke={bad === 'error' ? 'var(--color-error, #dc2626)' : st.stroke}
                  strokeWidth={active ? st.width + 1.6 : st.width}
                  strokeDasharray={quiet ? '3 3' : st.dash}
                  opacity={quiet ? 0.65 : 1}
                  markerEnd={`url(#pf-head-${EDGE_STYLE[e.kind] ? e.kind : 'work'})`}
                />
                {/* A chevron at the midpoint: on a long arrow the head at the
                    far end is not where the eye is. */}
                <path
                  d="M -4 -4 L 4 0 L -4 4"
                  transform={`translate(${g.mid.x},${g.mid.y}) rotate(${g.angle})`}
                  fill="none" stroke={st.stroke} strokeWidth={1.6}
                  strokeLinecap="round" strokeLinejoin="round" opacity={0.9}
                />
                <text x={g.mid.x} y={g.mid.y - 9} textAnchor="middle"
                  style={{
                    fontSize: 13, fontWeight: 600, fill: st.stroke,
                    pointerEvents: 'none', paintOrder: 'stroke',
                    stroke: 'var(--color-bg, #f4f6f9)', strokeWidth: 3.5,
                  }}>
                  {clip(text, 26)}{quiet ? ' · no task' : ''}
                </text>
                {bad && (
                  <circle cx={g.mid.x + 4} cy={g.mid.y + 12} r={4}
                    fill={bad === 'error' ? 'var(--color-error, #dc2626)' : 'var(--color-warning, #b45309)'} />
                )}
              </g>
            )
          })}

          {wire && byId.get(wire.from) && (
            <path
              d={`M ${byId.get(wire.from)!.x + NODE_W / 2} ${byId.get(wire.from)!.y + NODE_H / 2}
                  L ${wire.x} ${wire.y}`}
              stroke="var(--color-accent, #0061D0)" strokeWidth={2} strokeDasharray="5 4"
              fill="none" markerEnd="url(#pf-head-work)" />
          )}

          {nodes.map((n) => (
            <NodeShape
              key={n.id}
              node={n}
              title={titles[n.obj_name] || n.label || n.obj_name}
              selected={selection?.kind === 'node' && selection.id === n.id}
              problem={problemOf(n.id)}
              connecting={connecting}
              wiring={!!wire}
              onMouseDown={(ev) => {
                ev.stopPropagation()
                if (connecting) {
                  if (!wire) setWire({ from: n.id, x: n.x, y: n.y })
                  return
                }
                onSelect({ kind: 'node', id: n.id })
                const p = toDiagram(ev)
                moved.current = false
                setDrag({ id: n.id, dx: p.x - n.x, dy: p.y - n.y })
              }}
              onStartWire={(ev) => {
                ev.stopPropagation()
                const p = toDiagram(ev)
                setWire({ from: n.id, x: p.x, y: p.y })
              }}
              onDelete={() => onDeleteNode(n.id)}
            />
          ))}
        </g>
      </svg>

      {/* Toolbar. Connect is a labelled button, not a dot somebody has to
          discover — the first version's linking was missed entirely. */}
      <div style={{
        position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6,
        alignItems: 'center', flexWrap: 'wrap',
      }}>
        <button type="button" onClick={() => onConnectingChange(!connecting)}
          style={connecting ? toolOn : tool}
          title="Click one box, then the box the work goes to (Esc to stop)">
          {connecting ? 'Click the next box…' : '↳ Connect boxes'}
        </button>
        <div style={{ display: 'flex', gap: 2 }}>
          <button type="button" style={tool} onClick={() => setView((v) => ({
            ...v, z: Math.max(MIN_ZOOM, v.z * 0.9) }))} aria-label="Zoom out">−</button>
          <button type="button" style={tool} onClick={() => setView((v) => ({
            ...v, z: Math.min(MAX_ZOOM, v.z * 1.1) }))} aria-label="Zoom in">+</button>
          <button type="button" style={tool} onClick={fit}>Fit</button>
        </div>
        <span style={{
          fontSize: 13, color: 'var(--color-text-secondary)',
          background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
          borderRadius: 6, padding: '3px 7px',
        }}>
          {Math.round(view.z * 100)}%
        </span>
      </div>

      {nodes.length === 0 && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', pointerEvents: 'none',
          color: 'var(--color-text-secondary)', fontSize: 14.5, textAlign: 'center',
        }}>
          Pick a step, an entity or a data store on the left to start drawing.
        </div>
      )}
    </div>
  )
}

function NodeShape({
  node, title, selected, problem, connecting, wiring,
  onMouseDown, onStartWire, onDelete,
}: {
  node: FlowNode
  title: string
  selected: boolean
  problem?: 'error' | 'warning' | 'info'
  connecting: boolean
  wiring: boolean
  onMouseDown: (e: React.MouseEvent) => void
  onStartWire: (e: React.MouseEvent) => void
  onDelete: () => void
}) {
  const kind = (node.kind || 'step') as NodeKind
  const st = KIND_STYLE[kind] || KIND_STYLE.step
  const stroke = problem === 'error'
    ? 'var(--color-error, #dc2626)'
    : selected ? 'var(--color-accent, #0061D0)' : st.stroke
  const sub = kind === 'process'
    ? (node.entry_obj || node.flow || 'another process')
    : kind === 'external' ? 'outside the application' : node.obj_name

  return (
    <g style={{ cursor: connecting ? 'crosshair' : 'move' }} onMouseDown={onMouseDown}>
      <NodeOutline kind={kind} x={node.x} y={node.y} stroke={stroke} fill={st.fill}
        width={selected ? 2 : 1.4} />
      <text x={node.x + 12} y={node.y + 21}
        style={{ fontSize: 13.5, fontWeight: 600, fill: st.ink, pointerEvents: 'none' }}>
        {clip(title, 21)}
      </text>
      <text x={node.x + 12} y={node.y + 37}
        style={{
          fontSize: 13, pointerEvents: 'none',
          fill: 'var(--color-text-secondary, #465062)',
        }}>
        {clip(sub, 24)}
      </text>

      <text x={node.x + 8} y={node.y - 5}
        style={{
          fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase',
          fill: st.stroke, fontWeight: 700, pointerEvents: 'none',
        }}>
        {st.label}
      </text>

      {problem && (
        <circle cx={node.x + NODE_W - 9} cy={node.y + NODE_H - 9} r={4.5}
          fill={problem === 'error'
            ? 'var(--color-error, #dc2626)'
            : problem === 'warning' ? 'var(--color-warning, #b45309)' : 'var(--color-text-secondary)'} />
      )}

      {selected && !connecting && !wiring && (
        <g onMouseDown={onStartWire} style={{ cursor: 'crosshair' }}>
          <circle cx={node.x + NODE_W} cy={node.y + NODE_H / 2} r={7}
            fill="var(--color-accent, #0061D0)" stroke="var(--color-bg-secondary,#fff)"
            strokeWidth={2} />
          <text x={node.x + NODE_W + 12} y={node.y + NODE_H / 2 + 3.5}
            style={{
              fontSize: 13, fill: 'var(--color-accent, #0061D0)', fontWeight: 600,
              pointerEvents: 'none', paintOrder: 'stroke',
              stroke: 'var(--color-bg, #f4f6f9)', strokeWidth: 3,
            }}>
            drag to connect
          </text>
        </g>
      )}
      {selected && (
        <g onMouseDown={(e) => { e.stopPropagation(); onDelete() }} style={{ cursor: 'pointer' }}>
          <circle cx={node.x + NODE_W - 10} cy={node.y - 8} r={8}
            fill="var(--color-bg-secondary, #fff)" stroke="var(--color-border)" />
          <text x={node.x + NODE_W - 10} y={node.y - 4.5} textAnchor="middle"
            style={{ fontSize: 13, fill: 'var(--color-error, #dc2626)', pointerEvents: 'none' }}>
            ×
          </text>
        </g>
      )}
    </g>
  )
}

const tool: React.CSSProperties = {
  fontSize: 13, padding: '4px 9px', borderRadius: 6, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
  color: 'var(--color-text)', fontWeight: 500,
}
const toolOn: React.CSSProperties = {
  ...tool, background: 'var(--color-accent)', borderColor: 'var(--color-accent)',
  color: '#fff', fontWeight: 600,
}
